import React, { useContext, useState, useEffect } from "react";
import { Grid, Mui } from "@leap4/frontend";
import { DemandContext } from "./demandContext";
import ArrayInputComponent from "./arrayInputComponnent";
import GlobalBundleInput from "./globalBundleInput";
import { validateDemandForm } from "../../../utils/form/demandFormValidator";
import { FormChipsControlled } from "../../../formChipControl/FormChipsControlled";

const { FormHelperText, TextField, Typography } = Mui;

export interface IDetailsFormProps {
    mode: "create" | "edit";
    submitted: boolean; // flag passed from the parent
}

interface PubAccIdType {
    id: string;
    caId?: string;
    description: string;
}

interface IErrors {
    globalBundleWhiteList: string;
    globalBundleBlackList: string;
}

const DetailsForm = (props: IDetailsFormProps) => {
    const { demand, setDemand } = useContext(DemandContext);
    const [fieldErrors, setFieldErrors] = useState<{
        name?: string;
        AdvertisingSystem?: string;
        PubAccId?: string;
    }>({});
    const [globalError, setGlobalError] = useState<IErrors>({
        globalBundleWhiteList: "",
        globalBundleBlackList: ""
    });

    // Convert returned chip items to strings (filtering out empty ones)
    const handleExtraAdsUpdate = (items: any[]) => {
        const newItems = items
            .map((item: any) =>
                typeof item === "string" ? item : (item.key || item.value || "")
            )
            .filter((item) => item !== "");
        setDemand({ ...demand, extraAdsTxt: newItems });
    };

    // submission attempted -> validate the form and update errors.
    useEffect(() => {
        if (props.submitted) {
            const { errors } = validateDemandForm(demand);
            setFieldErrors(errors);
        }
    }, [demand, props.submitted]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setDemand({ ...demand, [name]: value });
    };

    return (
        <>
            <Typography
                fontFamily="Quicksand"
                fontWeight={600}
                variant="h5"
                style={{ paddingBottom: "20px" }}
            >
                Demand View
            </Typography>
            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-start"
                gap="20px"
            >
                <Grid item xs={3}>
                    <FormHelperText>Demand Partner Name*</FormHelperText>
                    <TextField
                        fullWidth
                        required
                        value={demand?.name || ""}
                        onChange={onChange}
                        name="name"
                        variant="outlined"
                        error={props.submitted && !!fieldErrors.name}
                        helperText={props.submitted ? (fieldErrors.name || "\u00A0") : "\u00A0"}
                        FormHelperTextProps={{
                            style: { minHeight: "24px", display: "block" }
                        }}
                    />
                </Grid>
                <Grid item xs={3}>
                    <FormHelperText>Advertising System*</FormHelperText>
                    <TextField
                        fullWidth
                        required
                        value={demand?.AdvertisingSystem || ""}
                        onChange={onChange}
                        name="AdvertisingSystem"
                        variant="outlined"
                        error={props.submitted && !!fieldErrors.AdvertisingSystem}
                        helperText={
                            props.submitted ? (fieldErrors.AdvertisingSystem || "\u00A0") : "\u00A0"
                        }
                        FormHelperTextProps={{
                            style: { minHeight: "24px", display: "block" }
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormHelperText>Lines*</FormHelperText>
                    <ArrayInputComponent<PubAccIdType>
                        fieldsConfig={{
                            id: { label: "PubAccId", required: true, gridSize: 3 },
                            caId: { label: "CAId", required: false, gridSize: 3.5 },
                            description: { label: "Description", required: false, gridSize: 3.5 },
                        }}
                        initialData={demand?.PubAccId}
                        onSubmit={(data) => setDemand({ ...demand, PubAccId: data })}
                    />
                    {props.submitted && (
                        <FormHelperText error style={{ minHeight: "24px", display: "block" }}>
                            {fieldErrors.PubAccId || "\u00A0"}
                        </FormHelperText>
                    )}
                </Grid>
                <Grid item xs={6}>
                    <GlobalBundleInput
                        title={`Global Bundle White List (${demand.globalBundleWhiteList?.length || 0})`}
                        error={globalError.globalBundleWhiteList}
                        setError={(error) =>
                            setGlobalError((prev) => ({ ...prev, globalBundleWhiteList: error }))
                        }
                        globalBundleList={demand.globalBundleWhiteList}
                        setBundleList={(list) => setDemand({ ...demand, globalBundleWhiteList: list })}
                        showExportButton={true}
                    />
                </Grid>
                <Grid item xs={6}>
                    <GlobalBundleInput
                        title={`Global Bundle Black List (${demand.globalBundleBlackList?.length || 0})`}
                        error={globalError.globalBundleBlackList}
                        setError={(error) =>
                            setGlobalError((prev) => ({ ...prev, globalBundleBlackList: error }))
                        }
                        globalBundleList={demand.globalBundleBlackList}
                        setBundleList={(list) => setDemand({ ...demand, globalBundleBlackList: list })}
                        showExportButton={true}
                    />
                </Grid>
                <Grid item xs = {6}>
                <FormChipsControlled
                        valueKey="key"
                        labelKey="value"
                        inputName="Extra ads.txt Lines"
                        optionsList={[]}
                        enableCreate={false}
                        pureCreate={true}
                        onUpdateItems={handleExtraAdsUpdate}
                        disableCloseOnSelect={false}
                        stackProps={{ width: "100%", height: "100%" }}
                        controlledValue={
                            demand.extraAdsTxt
                                ? demand.extraAdsTxt.map((line: string) => ({ key: line, value: line }))
                                : []
                        }
                    />
                </Grid>
            </Grid>
        </>
    );
};

export { DetailsForm };
